/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';

import { useTranslate } from 'react-admin';
import CustomSelect from '../../CustomSelect';
import {
  getManualResultEmptyLabel,
  getManualResultOptions,
  isManualResultParameter,
} from '../manualResults';

import './styles.scss';

interface TestResultData {
  name: string;
  parameters: any;
}

interface Range {
  color: string;
  description: string;
  interpretation: string;
  lowerLimit: number;
  lowerLimitOperator: string;
  unit: string;
  upperLimit: number;
  upperLimitOperator: string;
  displayInterpretation: string;
  rangeInterpretation: string;
}

const compareValuesFunctions = {
  '<=': (x: number, y: number) => x <= y,
  '<': (x: number, y: number) => x < y,
  '>=': (x: number, y: number) => x >= y,
  '>': (x: number, y: number) => x > y,
};

const getResultRangeData = (
  ranges: Range[] | undefined,
  measuredValue: number,
) =>
  ranges &&
  ranges.find((range) => {
    const { lowerLimitOperator, lowerLimit, upperLimit, upperLimitOperator } =
      range;
    const lowerLimitFunction =
      compareValuesFunctions[
        lowerLimitOperator as keyof typeof compareValuesFunctions
      ];
    const upperLimitFunction =
      compareValuesFunctions[
        upperLimitOperator as keyof typeof compareValuesFunctions
      ];

    if (lowerLimitFunction !== undefined && upperLimitFunction !== undefined) {
      return (
        lowerLimitFunction(measuredValue, lowerLimit) &&
        upperLimitFunction(measuredValue, upperLimit)
      );
    }

    return lowerLimitFunction(measuredValue, lowerLimit);
  });

const getResultDataForTestWithStringValue = (
  outcomes: any,
  stringValue: string,
) => {
  // Special cases when doctor has to see the original report to make a decision
  if (stringValue === 'seeOriginalReport' || stringValue === 'notSupported') {
    return {
      color: '#FFA500',
      description: stringValue,
      displayInterpretation: 'See lab result',
      name: stringValue,
    };
  }
  return outcomes.find((outcome: any) => outcome.name === stringValue);
};

const TestResult = ({
  data,
  testName,
  testParameter,
  testDefinition,
  isStringValueTest,
  manualResultValue,
  onManualResultChange,
  manualResultError,
  readonly,
}: {
  data: TestResultData;
  testName: string;
  testParameter: any;
  testDefinition: any;
  isStringValueTest: boolean;
  manualResultValue?: string;
  onManualResultChange?: (value: string) => void;
  manualResultError?: boolean;
  readonly?: boolean;
}) => {
  const translate = useTranslate();

  const handleManualResultChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    onManualResultChange?.(event.target.value);
  };

  // Test parameter name example: ferritin-male-18-110
  const testParameterName = testParameter.name;
  let resultData: any;
  const testParameterDefinition = testDefinition.parameters.find(
    (parameter: any) => parameter.name === testParameterName,
  );

  const parameterData = data.parameters.find(
    (parameter: any) => parameter.name === testParameterName,
  );

  if (isStringValueTest) {
    resultData = getResultDataForTestWithStringValue(
      testParameterDefinition?.outcomes,
      testParameter.stringValue,
    );
  } else {
    resultData = getResultRangeData(
      testParameterDefinition?.ranges,
      testParameter.measuredValue,
    );
  }

  let measuredValueToShow = testParameter.measuredValue;

  // Special case for lipoprotein test to show '< 7' instead of 6.99 because HL7 file sends <7 instead of a number, so we are saving 6.99 in the database, but <7 is what doctors need to see
  if (
    testParameterDefinition?.id === 'lipoprotein' &&
    testParameter.measuredValue === 6.99
  ) {
    measuredValueToShow = '< 7';
  }

  // Doctor has to pick the result (always for some tests, or when the lab
  // only sent a "see original report" value)
  const isManualResult =
    !readonly &&
    !!onManualResultChange &&
    isManualResultParameter(testName, testParameter.stringValue);

  return (
    <div>
      <div className="content">
        <div className="itemWrapper itemWrapper--stringValue itemWrapper--testName">
          <div className="label">{translate('testResults.parameterName')}</div>
          <div className="text">
            {translate(`parameters.${testParameterDefinition?.id}`)}
          </div>
        </div>
        {isStringValueTest && !isManualResult && (
          <div className="itemWrapper">
            <div className="label">{translate('testResults.result')}</div>
            <div
              className="text text--stringValue"
              style={{ background: resultData?.color }}
            >
              {resultData?.displayInterpretation}
            </div>
          </div>
        )}
        {isManualResult && (
          <div className="itemWrapper">
            <div className="label">{translate('testResults.result')}</div>
            <div className="text">
              <CustomSelect
                options={getManualResultOptions(
                  testName,
                  testParameterDefinition?.outcomes,
                )}
                value={manualResultValue || ''}
                onChange={handleManualResultChange}
                emptyLabel={getManualResultEmptyLabel(
                  testParameter.stringValue,
                )}
                error={manualResultError}
              />
            </div>
          </div>
        )}
        {!isStringValueTest && (
          <>
            <div className="itemWrapper itemWrapper--measuredValue">
              <div className="label">
                {translate('testResults.measuredValue')}
              </div>
              <div style={{ background: resultData?.color }} className="result">
                {measuredValueToShow}
              </div>
            </div>
            <div className="itemWrapper">
              <div className="label">{translate('testResults.unit')}</div>
              <div className="text">{resultData?.unit}</div>
            </div>
            <div className="itemWrapper">
              <div className="label">
                {translate('testResults.interpretation')}
              </div>
              <div className="text">
                {translate(
                  `resultInterpretation.${resultData?.interpretation}`,
                )}
              </div>
            </div>
            <div className="itemWrapper">
              <div className="label">{translate('testResults.range')}</div>
              <div className="text">
                {resultData?.rangeInterpretation
                  ? resultData?.rangeInterpretation
                  : `${resultData?.lowerLimit} - ${resultData?.upperLimit}`}
              </div>
            </div>
          </>
        )}
        {parameterData && parameterData.additionalInfo && (
          <div className="itemWrapper">
            <div className="label">
              {translate('testResults.additionalInfo')}
            </div>
            <div className="text">{parameterData.additionalInfo}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestResult;
